import {
    Box,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    useTheme,
} from "@mui/material";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxButton from "components/UI/AcxButton";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { HealthAndSafety } from "@mui/icons-material";
import { observer } from "mobx-react";
import { useStore } from "utils/useStore";
import {
    AcxTranscriptionStore,
    LastTranscriptionSelection,
} from "../Stores/AcxTranscriptionStore";
import { SpeakerStore } from "../Stores/SpeakerStore/SpeakerStore";
import { getSpeakerColorByChannelOrPersona } from "Theme/utils";
import type {
    ITranscriptionSegment,
    ManualTranscriptRedactionType,
} from "services/TranscriptionService";
import { ServiceError } from "services/BaseService";

enum REDACT_SCREENS {
    type = 0,
    confirm = 1,
}

type RedactionType = {
    key: string;
    display: string;
    redactedString: string;
    apiValue?: ManualTranscriptRedactionType;
};

const REDACTION_TYPES: Record<string, RedactionType> = {
    date: {
        key: "date",
        display: "Date",
        redactedString: "RedactedDate",
        apiValue: "Date",
    },
    creditCard: {
        key: "creditCard",
        display: "Credit Card",
        redactedString: "*****",
        apiValue: "CreditCard",
    },
    name: {
        key: "name",
        display: "Name",
        redactedString: "RedactedName",
        apiValue: "Name",
    },
    ssn: {
        key: "ssn",
        display: "SSN",
        redactedString: "*****",
        apiValue: "SSN",
    },
    address: {
        key: "address",
        display: "Address",
        redactedString: "RedactedAddress",
        apiValue: "Address",
    },
    phoneNumber: {
        key: "phoneNumber",
        display: "Phone Number",
        redactedString: "*****",
        apiValue: "PhoneNumber",
    },
    geoEntity: {
        key: "geoEntity",
        display: "Geopolitical Entity",
        redactedString: "RedactedGPE",
        apiValue: "GeopoliticalEntity",
    },
    email: {
        key: "email",
        display: "Email",
        redactedString: "RedactedEmail",
        apiValue: "Email",
    },
    url: {
        key: "url",
        display: "URL",
        redactedString: "RedactedURL",
        apiValue: "URL",
    },
    mrn: {
        key: "mrn",
        display: "Medical Record Number",
        redactedString: "RedactedMRN",
        apiValue: "MedicalRecordNumber",
    },
    custom: {
        key: "custom",
        display: "Custom",
        redactedString: "RedactedCustom",
        apiValue: "Custom",
    },
    none: { key: "none", display: "None", redactedString: "" },
};

const TYPE_SCREEN_TITLE = "Redact Selected Text";
const CONFIRM_SCREEN_TITLE = "Are You Sure?";
const CONTEXT_WORDS = 8;
const STALE_REDACTION_MESSAGE =
    "The transcript changed before this redaction could be saved. Please refresh the page and try again.";
const REDACTION_MARKERS = new Set([
    "*****",
    "RedactedAddress",
    "RedactedCustom",
    "RedactedDate",
    "RedactedEmail",
    "RedactedGPE",
    "RedactedMRN",
    "RedactedName",
    "RedactedURL",
]);

const areTypesEqual = (
    redactionType1: RedactionType,
    redactionType2: RedactionType,
) => {
    return redactionType1.key === redactionType2.key;
};

const isAllRedactionMarkers = (selectedText?: string) => {
    const words = selectedText?.trim().split(/\s+/).filter(Boolean) ?? [];
    return (
        words.length > 0 && words.every((word) => REDACTION_MARKERS.has(word))
    );
};

type SpeakerRedactionOption = {
    value: number;
    label: string;
    selectedText: string;
    startTime: number;
    endTime: number;
};

type SelectedTextRun = {
    channel: number;
    text: string;
};

const getSpeakerName = (channel: number, speakerStore?: SpeakerStore) =>
    speakerStore?.speakerChannelMap.get(channel)?.name ??
    speakerStore?.predictedSpeakerChannelMap.get(channel) ??
    `Speaker ${channel + 1}`;

const getWordsInSelection = (
    last: LastTranscriptionSelection | null,
    words: ITranscriptionSegment[],
) => {
    if (
        !last ||
        last.selectedStartTimestamp === null ||
        last.selectedEndTimestamp === null
    ) {
        return [];
    }

    const startTime = last.selectedStartTimestamp;
    const endTime = last.selectedEndTimestamp;
    return words.filter((word) => {
        const ws = word.startTime ?? 0;
        const we = word.endTime ?? 0;
        return ws >= startTime && we <= endTime;
    });
};

const buildSpeakerOptions = (
    last: LastTranscriptionSelection | null,
    words: ITranscriptionSegment[],
    speakerStore?: SpeakerStore,
) => {
    const selectedWords = getWordsInSelection(last, words);
    // Each option carries speaker-scoped text and timestamps so choosing a
    // speaker redacts that speaker's range, not the full multi-speaker selection.
    const channelSelections = new Map<
        number,
        { text: string[]; startTime: number; endTime: number }
    >();

    selectedWords.forEach((word) => {
        const existing = channelSelections.get(word.channel);
        if (!existing) {
            channelSelections.set(word.channel, {
                text: [word.text],
                startTime: word.startTime,
                endTime: word.endTime,
            });
            return;
        }

        existing.text.push(word.text);
        existing.startTime = Math.min(existing.startTime, word.startTime);
        existing.endTime = Math.max(existing.endTime, word.endTime);
    });

    const selectedChannel = last?.selectedChannel;
    return Array.from(channelSelections.entries())
        .map(([channel, selection]) => ({
            value: channel,
            label: getSpeakerName(channel, speakerStore),
            selectedText: selection.text.join(" "),
            startTime: selection.startTime,
            endTime: selection.endTime,
        }))
        .sort((a, b) => {
            if (a.value === selectedChannel) return -1;
            if (b.value === selectedChannel) return 1;
            return a.value - b.value;
        });
};

const buildSelectedTextRuns = (
    last: LastTranscriptionSelection | null,
    words: ITranscriptionSegment[],
) => {
    const selectedWords = getWordsInSelection(last, words);
    const runs: SelectedTextRun[] = [];

    selectedWords.forEach((word) => {
        let run = runs[runs.length - 1];
        if (!run || run.channel !== word.channel) {
            run = { channel: word.channel, text: word.text };
            runs.push(run);
            return;
        }

        run.text += " " + word.text;
    });

    return runs;
};

export const RedactDialog = observer(
    ({
        open,
        setOpen,
        speakerStore,
        onRedactSelection,
    }: {
        open: boolean;
        setOpen: (open: boolean) => void;
        speakerStore?: SpeakerStore;
        onRedactSelection?: (
            redactionType: ManualTranscriptRedactionType,
            selectedChannel?: number,
            selectedText?: string,
            selectedStartTime?: number,
            selectedEndTime?: number,
        ) => Promise<void>;
    }) => {
        const store = useStore(AcxTranscriptionStore);
        const speakerOptions = useMemo(
            () =>
                buildSpeakerOptions(
                    store.lastSelection,
                    store.transcriptWords ?? [],
                    speakerStore,
                ),
            [speakerStore, store.lastSelection, store.transcriptWords],
        );
        const selectedTextRuns = useMemo(
            () =>
                buildSelectedTextRuns(
                    store.lastSelection,
                    store.transcriptWords ?? [],
                ),
            [store.lastSelection, store.transcriptWords],
        );
        const [selectedChannel, setSelectedChannel] = useState<
            number | undefined
        >(undefined);
        const defaultSelectedChannel =
            speakerOptions.find(
                (option) => option.value === store.lastSelection?.selectedChannel,
            )?.value ?? speakerOptions[0]?.value;
        const effectiveSelectedChannel =
            selectedChannel ?? defaultSelectedChannel;
        const selectedSpeakerOption = speakerOptions.find(
            (option) => option.value === effectiveSelectedChannel,
        );
        const selectedText =
            selectedSpeakerOption?.selectedText ??
            store.lastSelection?.selectedText ??
            "";
        const selectedTextIsMarkerOnly = isAllRedactionMarkers(selectedText);
        const hasSpeakerChoice =
            !!store.lastSelection?.overlappingSpeakers ||
            speakerOptions.length > 1;

        const [screen, setScreen] = useState(REDACT_SCREENS.type);

        const [type, setType] = useState(REDACTION_TYPES.none);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState<string | null>(null);

        useEffect(() => {
            if (!open || speakerOptions.length === 0) {
                return;
            }

            setSelectedChannel((current) => {
                if (
                    current !== undefined &&
                    speakerOptions.some((option) => option.value === current)
                ) {
                    return current;
                }

                return (
                    speakerOptions.find(
                        (option) =>
                            option.value === store.lastSelection?.selectedChannel,
                    )?.value ?? speakerOptions[0].value
                );
            });
        }, [open, speakerOptions, store.lastSelection?.selectedChannel]);

        const close = () => {
            setOpen(false);
            setTimeout(() => {
                // Reset after the close animation so the type-selection screen
                // does not flash while the dialog is dismissing.
                setScreen(REDACT_SCREENS.type);
                setType(REDACTION_TYPES.none);
                setSelectedChannel(undefined);
                setError(null);
                setLoading(false);
                store.clearLastSelection();
            }, 250);
        };

        return (
            <AcxDialog
                isOpen={open}
                onClose={() => {
                    close();
                }}
                title={<Title screen={screen} />}
                dialogContentChildren={
                    <Grid container style={{ width: "40vh" }} spacing={2}>
                        {screen === REDACT_SCREENS.type && (
                            <TypeScreen
                                type={type}
                                setType={setType}
                                selectedText={selectedText}
                                selectedTextRuns={selectedTextRuns}
                                speakerStore={speakerStore}
                                markerOnly={selectedTextIsMarkerOnly}
                            />
                        )}
                        {screen === REDACT_SCREENS.confirm && (
                            <ConfirmScreen
                                redactedValue={type.redactedString}
                                speakerStore={speakerStore}
                                selectedChannel={effectiveSelectedChannel}
                                setSelectedChannel={setSelectedChannel}
                                speakerOptions={speakerOptions}
                                markerOnly={selectedTextIsMarkerOnly}
                                hasSpeakerChoice={hasSpeakerChoice}
                            />
                        )}
                        {error && (
                            <Grid item xs={12}>
                                <Typography color="error">{error}</Typography>
                            </Grid>
                        )}
                    </Grid>
                }
                children={
                    <AcxButton
                        color="primary"
                        onClick={async () => {
                            if (
                                screen === REDACT_SCREENS.type &&
                                type !== REDACTION_TYPES.none
                            ) {
                                // Advance to confirmation after the user chooses a redaction type.
                                setError(null);
                                setScreen(REDACT_SCREENS.confirm);
                            } else {
                                if (!type.apiValue || !onRedactSelection) {
                                    return;
                                }

                                try {
                                    const channelForRequest =
                                        hasSpeakerChoice
                                            ? effectiveSelectedChannel
                                            : undefined;
                                    setLoading(true);
                                    setError(null);
                                    await onRedactSelection(
                                        type.apiValue,
                                        channelForRequest,
                                        selectedText,
                                        channelForRequest !== undefined
                                            ? selectedSpeakerOption?.startTime
                                            : undefined,
                                        channelForRequest !== undefined
                                            ? selectedSpeakerOption?.endTime
                                            : undefined,
                                    );
                                    close();
                                } catch (err) {
                                    if (
                                        err instanceof ServiceError &&
                                        err.response.status === 409
                                    ) {
                                        setError(
                                            err.errorMessage ||
                                                STALE_REDACTION_MESSAGE,
                                        );
                                    } else if (err instanceof ServiceError) {
                                        setError(
                                            err.errorMessage ||
                                                "Redaction could not be saved.",
                                        );
                                    } else {
                                        setError(
                                            "Redaction could not be saved.",
                                        );
                                    }
                                } finally {
                                    setLoading(false);
                                }
                            }
                        }}
                        disabled={type === REDACTION_TYPES.none}
                        loading={loading}
                    >
                        {screen === REDACT_SCREENS.confirm ? "Confirm " : ""}
                        Redact
                        {screen === REDACT_SCREENS.confirm ? "ion" : ""}
                    </AcxButton>
                }
            />
        );
    },
);

const Title = ({ screen }) => {
    const theme = useTheme();
    return (
        <Grid
            sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
            }}
        >
            <Grid
                sx={{
                    backgroundColor: theme.palette.error["light"],
                    p: 1,
                    justifyContent: "center",
                    borderRadius: 1,
                    alignItems: "center",
                    display: "flex",
                }}
            >
                <HealthAndSafety color="error" />
            </Grid>
            {screen === REDACT_SCREENS.type
                ? TYPE_SCREEN_TITLE
                : CONFIRM_SCREEN_TITLE}
        </Grid>
    );
};

const TypeScreen = ({
    type,
    setType,
    selectedText,
    selectedTextRuns,
    speakerStore,
    markerOnly,
}: {
    type: RedactionType;
    setType: Dispatch<SetStateAction<RedactionType>>;
    selectedText: string;
    selectedTextRuns: SelectedTextRun[];
    speakerStore?: SpeakerStore;
    markerOnly: boolean;
}) => {
    return (
        <Grid item xs={12}>
            <Grid sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
                <Typography>
                    {markerOnly
                        ? "The selected redaction markers will be relabeled in the transcript. Audio will not be redacted again."
                        : "The text in the transcript and the audio segment will be redacted. This action is logged for audit."}
                </Typography>
                <SelectedTextBySpeaker
                    selectedText={selectedText}
                    selectedTextRuns={selectedTextRuns}
                    speakerStore={speakerStore}
                />
                <Grid>
                    <Typography sx={{ mb: 1 }}>Tag this redaction</Typography>
                    <Grid
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "wrap",
                            columnGap: 2,
                            rowGap: 1,
                        }}
                    >
                        {Object.values(REDACTION_TYPES).map((redactionType) => {
                            if (
                                areTypesEqual(
                                    REDACTION_TYPES.none,
                                    redactionType,
                                )
                            )
                                return null;
                            return (
                                <AcxButton
                                    key={redactionType.key}
                                    color={
                                        areTypesEqual(type, redactionType)
                                            ? "primary"
                                            : "white"
                                    }
                                    variant="outlined"
                                    onClick={() => setType(redactionType)}
                                >
                                    {redactionType.display}
                                </AcxButton>
                            );
                        })}
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

const SelectedTextBySpeaker = ({
    selectedText,
    selectedTextRuns,
    speakerStore,
}: {
    selectedText: string;
    selectedTextRuns: SelectedTextRun[];
    speakerStore?: SpeakerStore;
}) => {
    const theme = useTheme();

    return (
        <Grid
            sx={{
                width: "100%",
                borderRadius: 1,
                backgroundColor: theme.palette.gray["200"],
                py: 2,
                px: 2,
                borderLeft: 8,
                borderLeftColor: theme.palette.error["500"],
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
            }}
        >
            {selectedTextRuns.length > 0 ? (
                selectedTextRuns.map((run, runIdx) => (
                    <Grid
                        key={`${run.channel}-${runIdx}`}
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: 1.5,
                            alignItems: "baseline",
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: "bold",
                                color: getSpeakerColorByChannelOrPersona(
                                    run.channel,
                                ),
                                minWidth: "100px",
                                flexShrink: 0,
                            }}
                        >
                            {getSpeakerName(run.channel, speakerStore)}
                        </Typography>
                        <Typography
                            sx={{
                                wordBreak: "break-word",
                                lineHeight: 1.6,
                                flex: 1,
                            }}
                        >
                            {run.text}
                        </Typography>
                    </Grid>
                ))
            ) : (
                <Typography sx={{ wordBreak: "break-word", lineHeight: 1.6 }}>
                    {selectedText}
                </Typography>
            )}
        </Grid>
    );
};

type PreviewSegment = { type: "text"; text: string } | { type: "redacted" };
type PreviewRun = {
    channel: number;
    segments: PreviewSegment[];
};

const RedactedPill = ({ value }: { value: string }) => {
    const theme = useTheme();
    return (
        <Box
            component="span"
            sx={{
                backgroundColor: theme.palette.error["500"],
                color: theme.palette.error.contrastText,
                px: 0.75,
                py: 0.25,
                borderRadius: 0.5,
                fontWeight: "bold",
            }}
        >
            {value}
        </Box>
    );
};

const RedactedPreviewText = ({ value }: { value: string }) => {
    const theme = useTheme();
    return (
        <Box
            component="span"
            sx={{
                color: theme.palette.error["600"],
                fontWeight: "bold",
            }}
        >
            {value}
        </Box>
    );
};

const ConfirmScreen = observer(
    ({
        redactedValue,
        speakerStore,
        selectedChannel,
        setSelectedChannel,
        speakerOptions,
        markerOnly,
        hasSpeakerChoice,
    }: {
        redactedValue: string;
        speakerStore?: SpeakerStore;
        selectedChannel?: number;
        setSelectedChannel: (channel: number | undefined) => void;
        speakerOptions: SpeakerRedactionOption[];
        markerOnly: boolean;
        hasSpeakerChoice: boolean;
    }) => {
        const theme = useTheme();
        const store = useStore(AcxTranscriptionStore);
        const last = store.lastSelection;
        const words = store.transcriptWords ?? [];

        const preview = (() => {
            if (
                !last ||
                last.selectedStartTimestamp === null ||
                last.selectedEndTimestamp === null ||
                !words.length
            ) {
                return null;
            }

            const startTime = last.selectedStartTimestamp;
            const endTime = last.selectedEndTimestamp;

            const inSelection = (idx: number) => {
                const w = words[idx];
                const ws = w.startTime ?? 0;
                const we = w.endTime ?? 0;
                const isSelectedSpeaker =
                    selectedChannel === undefined ||
                    w.channel === selectedChannel;
                return ws >= startTime && we <= endTime && isSelectedSpeaker;
            };

            const inSelectedTimeWindow = (idx: number) => {
                const w = words[idx];
                const ws = w.startTime ?? 0;
                const we = w.endTime ?? 0;
                return ws >= startTime && we <= endTime;
            };

            let firstIdx = -1;
            for (let i = 0; i < words.length; i++) {
                if (inSelectedTimeWindow(i)) {
                    firstIdx = i;
                    break;
                }
            }
            if (firstIdx === -1) return null;

            let lastIdx = firstIdx;
            for (let i = words.length - 1; i >= firstIdx; i--) {
                if (inSelectedTimeWindow(i)) {
                    lastIdx = i;
                    break;
                }
            }

            const beforeStart = Math.max(0, firstIdx - CONTEXT_WORDS);
            const afterEnd = Math.min(
                words.length - 1,
                lastIdx + CONTEXT_WORDS,
            );

            const runs: PreviewRun[] = [];
            for (let i = beforeStart; i <= afterEnd; i++) {
                const word = words[i];
                const isRedacted = inSelection(i);
                let run = runs[runs.length - 1];
                if (!run || run.channel !== word.channel) {
                    run = { channel: word.channel, segments: [] };
                    runs.push(run);
                }
                const lastSeg = run.segments[run.segments.length - 1];
                if (isRedacted) {
                    run.segments.push({ type: "redacted" });
                } else if (lastSeg && lastSeg.type === "text") {
                    lastSeg.text += " " + word.text;
                } else {
                    run.segments.push({ type: "text", text: word.text });
                }
            }

            return {
                runs,
                hasMoreBefore: beforeStart > 0,
                hasMoreAfter: afterEnd < words.length - 1,
            };
        })();

        return (
            <Grid item xs={12}>
                <Typography sx={{ mb: 2 }}>
                    {markerOnly
                        ? "This action is irreversible, the transcription file will be changed. Confirm this is the correct relabel before saving."
                        : "This action is irreversible, the audio and transcription files will be changed. Confirm this is the correct change before redacting."}
                </Typography>
                <Grid
                    sx={{
                        width: "100%",
                        borderRadius: 1,
                        backgroundColor: theme.palette.gray["200"],
                        py: 2,
                        px: 2,
                        borderLeft: 8,
                        borderLeftColor: theme.palette.error["500"],
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                    }}
                >
                    {preview ? (
                        <>
                            {preview.hasMoreBefore && (
                                <Typography
                                    sx={{ color: theme.palette.gray[400] }}
                                >
                                    …
                                </Typography>
                            )}
                            {preview.runs.map((run, runIdx) => (
                                <Grid
                                    key={runIdx}
                                    sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        gap: 1.5,
                                        alignItems: "baseline",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: "bold",
                                            color: getSpeakerColorByChannelOrPersona(
                                                run.channel,
                                            ),
                                            minWidth: "100px",
                                            flexShrink: 0,
                                        }}
                                    >
                                        {getSpeakerName(
                                            run.channel,
                                            speakerStore,
                                        )}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            wordBreak: "break-word",
                                            lineHeight: 1.6,
                                            flex: 1,
                                        }}
                                    >
                                        {run.segments.map((seg, segIdx) => {
                                            if (seg.type === "redacted") {
                                                return (
                                                    <span key={segIdx}>
                                                        {segIdx > 0 && " "}
                                                        <RedactedPreviewText
                                                            value={
                                                                redactedValue
                                                            }
                                                        />
                                                    </span>
                                                );
                                            }
                                            return (
                                                <span key={segIdx}>
                                                    {segIdx > 0 && " "}
                                                    {seg.text}
                                                </span>
                                            );
                                        })}
                                    </Typography>
                                </Grid>
                            ))}
                            {preview.hasMoreAfter && (
                                <Typography
                                    sx={{ color: theme.palette.gray[400] }}
                                >
                                    …
                                </Typography>
                            )}
                        </>
                    ) : (
                        <RedactedPill value={redactedValue} />
                    )}
                </Grid>
                {hasSpeakerChoice && speakerOptions.length > 1 && (
                    <Grid sx={{ mt: 2 }}>
                        <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                            Multiple speakers detected
                        </Typography>
                        <FormControl fullWidth size="small">
                            <InputLabel id="redact-speaker-select-label">
                                Speaker to redact
                            </InputLabel>
                            <Select
                                labelId="redact-speaker-select-label"
                                value={selectedChannel ?? ""}
                                label="Speaker to redact"
                                onChange={(event) =>
                                    setSelectedChannel(
                                        Number(event.target.value),
                                    )
                                }
                            >
                                {speakerOptions.map((option) => (
                                    <MenuItem
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Typography
                            sx={{ mt: 1, color: theme.palette.gray[500] }}
                            variant="body2"
                        >
                            {markerOnly
                                ? "Only the selected speaker's redaction markers will be relabeled. Audio will not be redacted again."
                                : "The transcript redaction will apply to the selected speaker. The full selected audio interval will still be redacted."}
                        </Typography>
                    </Grid>
                )}
            </Grid>
        );
    },
);
